import React, { Component } from "react";
import Recipe from "./Recipe"; // Will create this next
import axios from "axios";
import { v4 as uuid } from "uuid";
import {
  calculateGrams,
  UNIT_TO_GRAMS_SIMPLE,
  UNIT_LIST,
} from "./utility/unitConversions"; // Adjust path
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const config = require("../config.json");

export default class RecipeAdmin extends Component {
  constructor(props) {
    super(props);
    // Bind necessary methods
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleRecipeIngredientChange =
      this.handleRecipeIngredientChange.bind(this);
    this.addRecipeIngredientRow = this.addRecipeIngredientRow.bind(this);
    this.removeRecipeIngredientRow = this.removeRecipeIngredientRow.bind(this);
    this.handleAddRecipe = this.handleAddRecipe.bind(this);
    this.handleUpdateRecipe = this.handleUpdateRecipe.bind(this);
    this.handleDeleteRecipe = this.handleDeleteRecipe.bind(this);
  }

  state = {
    // State for the 'Add New Recipe' form
    newRecipe: {
      id: uuid(), // Pre-generate ID
      name: "",
      name_en: "",
      description: "",
      description_en: "",
      // Add other recipe metadata fields here (e.g., servings, prepTime, instructions...)
      recipeIngredients: [], // Array to hold ingredient line items for the new recipe
      image: "", // New field
      categories: [],
      url: "", // New field
      portions: "",
      totalPreparationTime: 0, // New field (e.g., "30 min", "1 hr")
      activeCookingTime: 0, // New field (e.g., "15 min")
    },
    recipes: [], // Full list fetched from API
    shownRecipes: [], // List currently displayed
    ingredients: [], // For ingredient selection dropdowns
    recipeImages: [], // New state for image selection dropdown
    recipeCategories: [],
    loading: false,
    loadingIngredients: false,
    loadingImages: false,
  };

  // --- Data Fetching ---

  fetchRecipeImages = async () => {
    // New function
    this.setState({ loadingImages: true });
    try {
      const res = await axios.get(
        // Adjust bucketName if it's different for recipe images
        `${config.api.invokeUrl}/images?bucketName=${
          config.s3.recipeImagesBucket || "your-recipe-images-bucket-name"
        }`
      );
      if (res.data?.Contents) {
        this.setState({
          recipeImages: res.data.Contents,
          loadingImages: false,
        });
      } else {
        this.setState({ recipeImages: [], loadingImages: false });
      }
    } catch (err) {
      console.error("Error fetching recipe images:", err);
      this.setState({ loadingImages: false });
    }
  };

  fetchRecipes = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.get(`${config.api.invokeUrl}/cookbook/recipes`); // Adjust endpoint if needed
      const recipes = res.data || [];
      const recipeCategories = [];

      for (const recipe of recipes) {
        for (const category of recipe.categories) {
          recipeCategories.push(category);
        }
      }

      const uniqueCategories = [...new Set(recipeCategories)];

      this.setState({
        recipes: recipes,
        shownRecipes: recipes,
        loading: false,
      });

      this.props.setRecipeCategories(uniqueCategories);
    } catch (err) {
      console.error("Error fetching recipes:", err);
      this.setState({ loading: false });
    }
  };

  fetchIngredients = async () => {
    this.setState({ loadingIngredients: true });
    try {
      // Assuming you have the getIngredients Lambda from before
      const res = await axios.get(
        `${config.api.invokeUrl}/cookbook/ingredients`
      );
      const ingredients = res.data || [];
      this.setState({ ingredients: ingredients, loadingIngredients: false });
    } catch (err) {
      console.error("Error fetching ingredients:", err);
      this.setState({ loadingIngredients: false });
    }
  };

  // --- CRUD Operations ---

  handleAddRecipe = async (recipeData, event) => {
    event.preventDefault();
    if (recipeData.recipeIngredients.length === 0) {
      alert("Tilføj venligst mindst én ingrediens til opskriften.");
      return;
    }
    this.setState({ loading: true });

    // Prepare the ingredients array for the backend
    // Remove temporary _id, ensure required fields are present
    const ingredientsPayload = recipeData.recipeIngredients
      .filter((item) => item.ingredientId) // Ensure an ingredient was selected
      .map(({ _id, ingredientName, ...rest }) => ({
        ...rest,
        // Ensure numbers are numbers (Backend Lambda might also do this)
        displayAmount: parseFloat(rest.displayAmount) || 0,
        grams: parseFloat(rest.grams) || 0, // Use 0 if calculation failed and wasn't entered manually
      }));

    if (ingredientsPayload.length !== recipeData.recipeIngredients.length) {
      alert(
        "En eller flere ingredienslinjer mangler en valgt ingrediens og vil blive ignoreret."
      );
    }
    if (ingredientsPayload.length === 0) {
      alert(
        "Ingen gyldige ingredienser fundet. Vælg venligst en ingrediens for hver linje."
      );
      this.setState({ loading: false });
      return;
    }

    try {
      const params = {
        id: recipeData.id, // Use the pre-generated ID or let backend generate
        name: recipeData.name,
        name_en: recipeData.name_en,
        description: recipeData.description,
        description_en: recipeData.description_en,
        recipeIngredients: ingredientsPayload, // Send the processed list
        image: recipeData.image,
        categories: recipeData.categories,
        totalPreparationTime: recipeData.totalPreparationTime,
        activeCookingTime: recipeData.activeCookingTime,
        portions: recipeData.portions,
        url: recipeData.url,
      };

      // POST to the recipes endpoint
      await axios.post(
        `${config.api.invokeUrl}/cookbook/recipes/${recipeData.id}`, // Or just /recipes
        params
      );

      // Refresh list or add optimistically
      this.fetchRecipes(); // Simplest way to refresh
      this.fetchRecipeImages();
      this.setState({
        // Reset form
        newRecipe: {
          id: uuid(),
          name: "",
          name_en: "",
          description: "",
          description_en: "",
          image: "",
          categories: [],
          portions: "",
          url: "",
          totalPreparationTime: "",
          activeCookingTime: "",
          recipeIngredients: [],
        },
        // loading state handled by fetchRecipes
      });
    } catch (err) {
      console.error("Error adding recipe:", err);
      this.setState({ loading: false });
      alert(`Fejl under tilføjelse af opskrift: ${err.message}`);
    }
  };

  handleUpdateRecipe = async (recipeData) => {
    // This function is called from the Recipe component
    if (recipeData.recipeIngredients.length === 0) {
      alert("En opskrift skal have mindst én ingrediens.");
      return; // Or handle as needed
    }
    this.setState({ loading: true });

    // Prepare payload similarly to handleAddRecipe
    const ingredientsPayload = recipeData.recipeIngredients
      .filter((item) => item.ingredientId)
      .map(({ _id, ingredientName, ...rest }) => ({
        ...rest,
        displayAmount: parseFloat(rest.displayAmount) || 0,
        grams: parseFloat(rest.grams) || 0,
      }));

    if (ingredientsPayload.length !== recipeData.recipeIngredients.length) {
      alert(
        "En eller flere ingredienslinjer mangler en valgt ingrediens og vil blive ignoreret ved opdatering."
      );
    }
    if (ingredientsPayload.length === 0) {
      alert("Ingen gyldige ingredienser fundet til opdatering.");
      // Potentially revert changes or let user fix, depends on desired UX
      this.setState({ loading: false });
      return;
    }

    try {
      const params = {
        id: recipeData.id, // ID must match
        name: recipeData.name,
        name_en: recipeData.name_en,
        description: recipeData.description,
        description_en: recipeData.description_en,
        image: recipeData.image ?? "",
        recipeIngredients: ingredientsPayload,
        image: recipeData.image,
        categories: recipeData.categories,
        totalPreparationTime: recipeData.totalPreparationTime,
        activeCookingTime: recipeData.activeCookingTime,
        portions: recipeData.portions,
        url: recipeData.url,
      };

      await axios.patch(
        // Or PUT
        `${config.api.invokeUrl}/cookbook/recipes/${recipeData.id}`,
        params
      );

      this.fetchRecipes(); // Refresh list
      // loading state handled by fetchRecipes
    } catch (err) {
      console.error("Error updating recipe:", err);
      this.setState({ loading: false });
      alert(`Fejl under opdatering af opskrift: ${err.message}`);
    }
  };

  handleDeleteRecipe = async (id, event) => {
    event.preventDefault();
    if (
      window.confirm(
        `Er du sikker på, at du vil slette denne opskrift (${id}) permanent?`
      )
    ) {
      this.setState({ loading: true });
      try {
        await axios.delete(`${config.api.invokeUrl}/cookbook/recipes/${id}`);
        this.fetchRecipes(); // Refresh
      } catch (err) {
        console.error("Error deleting recipe:", err);
        this.setState({ loading: false });
        alert(`Fejl under sletning af opskrift: ${err.message}`);
      }
    }
  };

  // --- Ingredient Row Management for New Recipe Form ---

  addRecipeIngredientRow() {
    const newIngredientRow = {
      _id: uuid(), // Temporary client-side ID
      ingredientId: "",
      ingredientName: "",
      displayAmount: "",
      displayUnit: "g", // Default unit
      grams: "",
    };
    this.setState((prevState) => ({
      newRecipe: {
        ...prevState.newRecipe,
        recipeIngredients: [
          ...prevState.newRecipe.recipeIngredients,
          newIngredientRow,
        ],
      },
    }));
  }

  removeRecipeIngredientRow(index) {
    this.setState((prevState) => ({
      newRecipe: {
        ...prevState.newRecipe,
        recipeIngredients: prevState.newRecipe.recipeIngredients.filter(
          (_, i) => i !== index
        ),
      },
    }));
  }

  // Handles changes within a specific ingredient row in the new recipe form
  handleRecipeIngredientChange(index, fieldName, value) {
    this.setState((prevState) => {
      const updatedIngredients = [...prevState.newRecipe.recipeIngredients];
      const currentIngredient = { ...updatedIngredients[index] };

      let newGrams = currentIngredient.grams; // Keep current grams by default

      // Handle react-select object for ingredientId
      if (fieldName === "ingredientId") {
        const selectedIngredient = this.state.ingredients.find(
          (ing) => ing.id === value
        );
        currentIngredient.ingredientId = value || ""; // Store the ID (value)
        currentIngredient.ingredientName = selectedIngredient
          ? selectedIngredient.name
          : "";
      } else {
        // Handle regular input fields
        currentIngredient[fieldName] = value;
      }

      // Auto-calculate grams if displayAmount or displayUnit changes,
      // unless grams was the field being directly edited.
      // Recalculate even if ingredientId changed, if amount/unit already exist
      if (fieldName !== "grams") {
        const calculated = calculateGrams(
          currentIngredient.displayAmount,
          currentIngredient.displayUnit
        );
        // Only update if calculation is possible, otherwise keep existing (potentially manual) value
        if (calculated !== "") {
          newGrams = calculated;
        } else if (
          fieldName === "displayUnit" ||
          fieldName === "displayAmount"
        ) {
          // If unit/amount changed and calculation failed (e.g., unit 'stk'), clear auto-grams
          newGrams = "";
        }
      } else {
        // If 'grams' field itself was changed, use its value directly
        newGrams = value;
      }
      currentIngredient.grams = newGrams;

      updatedIngredients[index] = currentIngredient;

      return {
        newRecipe: {
          ...prevState.newRecipe,
          recipeIngredients: updatedIngredients,
        },
      };
    });
  }

  // --- General Input Handling for Top-Level Recipe Fields ---

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      newRecipe: {
        ...prevState.newRecipe,
        [name]: value,
      },
    }));
  };

  handleCategoriesChange = (selectedOptions) => {
    const categoriesArray = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    // Update this.state.newRecipe.categories or this.state.updatedRecipe.categories
    this.setState((prevState) => ({
      newRecipe: {
        // or updatedRecipe
        ...prevState.newRecipe, // or updatedRecipe
        categories: categoriesArray,
      },
    }));
  };

  // --- Lifecycle ---

  componentDidMount() {
    this.fetchRecipes();
    this.fetchIngredients(); // Fetch ingredients needed for dropdowns
    this.fetchRecipeImages();
    this.setState({ recipeCategories: this.props.recipeCategories });
  }

  // --- Render ---

  renderNewRecipeIngredientForm(ingredientItem, index) {
    // Prepare options for react-select
    const ingredientSelectOptions = this.state.ingredients.map((ing) => ({
      value: ing.id, // The value to store (ID)
      label: `${ing.name} (${ing.category || "Ukategoriseret"})`, // What the user sees
    }));

    // Find the currently selected option object for react-select's value prop
    const currentIngredientValue = ingredientSelectOptions.find(
      (option) => option.value === ingredientItem.ingredientId
    );

    const unitOptions = UNIT_LIST.map((unit) => (
      <option key={unit} value={unit}>
        {unit}
      </option>
    ));

    return (
      <div key={ingredientItem._id} className="box recipe-ingredient-row mb-3">
        <div className="columns is-mobile is-vcentered">
          {/* === Ingredient Selection (react-select) === */}
          <div className="column is-4">
            <div className="field">
              <label className="label is-small">Ingrediens</label>
              <div className="control">
                <Select
                  options={ingredientSelectOptions}
                  value={currentIngredientValue}
                  onChange={(selectedOption) =>
                    // Pass the selected value (ingredientId) to the handler
                    this.handleRecipeIngredientChange(
                      index,
                      "ingredientId",
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  placeholder="Søg/vælg ingrediens..."
                  isClearable // Allow clearing selection
                  isSearchable
                  isLoading={this.state.loadingIngredients}
                  noOptionsMessage={() => "Ingen ingredienser fundet"}
                  // Add classNamePrefix="react-select" for potential Bulma styling later
                  // styles={{ control: base => ({ ...base, minHeight: '30px', height: '30px' }) }} // Basic height adjustment
                />
              </div>
            </div>
          </div>

          {/* Display Amount */}
          <div className="column is-2">
            {/* ... (same as before) ... */}
            <div className="field">
              <label className="label is-small">Mængde</label>
              <div className="control">
                <input
                  className="input is-small"
                  type="number"
                  step="any"
                  placeholder="fx 2"
                  value={ingredientItem.displayAmount}
                  onChange={(e) =>
                    this.handleRecipeIngredientChange(
                      index,
                      "displayAmount",
                      e.target.value
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Display Unit */}
          <div className="column is-2">
            {/* ... (same as before) ... */}
            <div className="field">
              <label className="label is-small">Enhed</label>
              <div className="control">
                <div className="select is-small is-fullwidth">
                  <select
                    value={ingredientItem.displayUnit}
                    onChange={(e) =>
                      this.handleRecipeIngredientChange(
                        index,
                        "displayUnit",
                        e.target.value
                      )
                    }
                  >
                    {unitOptions}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Grams (Calculated/Manual) */}
          <div className="column is-3">
            {/* ... (same as before) ... */}
            <div className="field">
              <label className="label is-small">Gram (til beregning)</label>
              <div className="control">
                <input
                  className="input is-small"
                  type="number"
                  step="any"
                  placeholder="Auto/Manuel"
                  value={ingredientItem.grams}
                  onChange={(e) =>
                    this.handleRecipeIngredientChange(
                      index,
                      "grams",
                      e.target.value
                    )
                  }
                  title="Beregnes automatisk, men kan overskrives manuelt hvis nødvendigt (fx for mel)"
                />
              </div>
            </div>
          </div>

          {/* Remove Button */}
          <div className="column is-1">
            {/* ... (same as before) ... */}
            <button
              type="button"
              className="button is-danger is-small is-outlined mt-4"
              onClick={() => this.removeRecipeIngredientRow(index)}
              aria-label="Fjern ingrediens"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, loadingIngredients } = this.state;

    if (loadingIngredients) {
      return <p>Henter ingredienser...</p>;
    }

    return (
      <>
        <p className="subtitle is-5">Administrer Opskrifter</p>
        {loading && <p>Arbejder...</p>}
        <br />
        <div className="columns is-multiline">
          {/* Column for Adding New Recipe */}
          <div className="column is-full">
            {" "}
            {/* Make form take full width initially */}
            <form
              onSubmit={(event) =>
                this.handleAddRecipe(this.state.newRecipe, event)
              }
            >
              <h4 className="title is-4">Tilføj Ny Opskrift</h4>

              {/* Basic Recipe Info */}
              <div className="columns">
                <div className="column is-half">
                  <div className="field">
                    <label className="label">Navn (Dansk)</label>
                    <div className="control">
                      <input
                        name="name"
                        className="input"
                        type="text"
                        placeholder="fx Pandekager"
                        value={this.state.newRecipe.name}
                        onChange={this.handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Beskrivelse (Dansk)</label>
                    <div className="control">
                      <textarea
                        name="description"
                        className="textarea"
                        placeholder="Kort beskrivelse..."
                        value={this.state.newRecipe.description}
                        onChange={this.handleInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="column is-half">
                  <div className="field">
                    <label className="label">Navn (Engelsk)</label>
                    <div className="control">
                      <input
                        name="name_en"
                        className="input"
                        type="text"
                        placeholder="e.g., Pancakes"
                        value={this.state.newRecipe.name_en}
                        onChange={this.handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">Beskrivelse (Engelsk)</label>
                    <div className="control">
                      <textarea
                        name="description_en"
                        className="textarea"
                        placeholder="Short description..."
                        value={this.state.newRecipe.description_en}
                        onChange={this.handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="label">Kategorier</label>
                <CreatableSelect
                  isMulti
                  options={[]}
                  value={this.state.newRecipe.categories.map((cat) => ({
                    value: cat,
                    label: cat,
                  }))}
                  onChange={this.handleCategoriesChange}
                  placeholder="Tilføj kategorier..."
                  formatCreateLabel={(inputValue) => `Opret "${inputValue}"`}
                />
              </div>

              <div className="field">
                <label className="label">Billede</label>
                <div className="control">
                  <div
                    className={`select ${
                      this.state.loadingImages ? "is-loading" : ""
                    }`}
                  >
                    <select
                      name="image"
                      value={this.state.newRecipe.image}
                      onChange={this.handleInputChange}
                      disabled={this.state.loadingImages}
                    >
                      <option value="">Intet billede</option>
                      {this.state.recipeImages.map((img) => (
                        <option key={img.Key} value={img.Key}>
                          {img.Key}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {/* Add fields for servings, prepTime, instructions etc. here */}
              <div className="columns">
                <div className="column is-half">
                  {/* Total Preparation Time */}
                  <div className="field">
                    <label className="label">Total Forberedelsestid</label>
                    <input
                      name="totalPreparationTime"
                      className="input"
                      type="text"
                      placeholder="Fx 45 (minutter)"
                      value={this.state.newRecipe.totalPreparationTime}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
                <div className="column is-half">
                  {/* Active Cooking Time */}
                  <div className="field">
                    <label className="label">Aktiv Tilberedningstid</label>
                    <input
                      name="activeCookingTime"
                      className="input"
                      type="text"
                      placeholder="Fx 30 (minutter)"
                      value={this.state.newRecipe.activeCookingTime}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="columns">
                <div className="column is-half">
                  <div className="field">
                    <label className="label">Opskrifts URL</label>
                    <input
                      name="url"
                      className="input"
                      type="url"
                      placeholder="fx https://minopskrift.dk/pandekager"
                      value={this.state.newRecipe.url}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
                <div className="column is-half">
                  <div className="field">
                    <label className="label">Antal Portioner</label>
                    <input
                      name="portions"
                      className="input"
                      type="number"
                      min="1"
                      placeholder="fx 4"
                      value={this.state.newRecipe.portions}
                      onChange={this.handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Dynamic Ingredient List Section */}
              <h5 className="title is-5 mt-4">Ingredienser</h5>
              {this.state.newRecipe.recipeIngredients.map((item, index) =>
                this.renderNewRecipeIngredientForm(item, index)
              )}
              <button
                type="button" // Important: prevent form submission
                className="button is-info is-outlined mb-4"
                onClick={this.addRecipeIngredientRow}
                disabled={
                  loadingIngredients || this.state.ingredients.length === 0
                }
              >
                + Tilføj Ingrediens Linje
              </button>
              {this.state.ingredients.length === 0 && !loadingIngredients && (
                <p className="has-text-danger">
                  Ingen ingredienser fundet. Kan ikke tilføje linjer.
                </p>
              )}

              {/* Submit Button */}
              <div className="field">
                <div className="control">
                  <button
                    type="submit"
                    className="button is-primary is-medium"
                    disabled={loading}
                  >
                    Gem Ny Opskrift
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Filtering/Display Existing Recipes Section */}
          <div className="column is-full">
            <hr />
            <h4 className="title is-4">Eksisterende Opskrifter</h4>
            {/* Add filtering controls here if needed */}
          </div>
        </div>

        {/* Display Existing Recipes */}
        <div className="tile is-ancestor">
          <div className="tile is-parent is-vertical">
            {this.state.shownRecipes.length > 0
              ? this.state.shownRecipes.map((recipe) => (
                  <Recipe // Use the Recipe component
                    handleUpdateRecipe={this.handleUpdateRecipe}
                    handleDeleteRecipe={this.handleDeleteRecipe}
                    // handleAddRecipe={this.handleAddRecipe} // For copy functionality if needed in Recipe.js
                    recipe={recipe}
                    ingredients={this.state.ingredients} // Pass ingredients for edit mode dropdown
                    key={recipe.id}
                    isAdmin={true}
                    className="tile is-child"
                    recipeImages={this.state.recipeImages}
                  />
                ))
              : !loading && <p>Ingen opskrifter fundet.</p>}
          </div>
        </div>
      </>
    );
  }
}
